import { Component, computed, inject, input, output, signal } from '@angular/core';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { PlayerService } from 'app/features/player/data-access/player.service';

import { IEpisodeDetailsDto } from '@features/assets/data-access/series-api.dto';
import { SettingsStore } from '@core/settings';
import { PlayerTypes } from 'app/features/player/data-access/player.config';
import { ConverterHelper } from '@shared/utils';
import { PlayerItem } from 'app/features/player/data-access/player.model';
import { BadgeComponent } from 'app/shared/components/badge/badge.component';
import { BadgeVariant } from 'app/shared/components/badge/badge.model';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';

@Component({
  selector: 'player-controls-series-episode',
  imports: [
    RoundedIconButton,
    BadgeComponent,
    TranscodeImageDirective
],
  templateUrl: './player-controls-series-episode.html',
  styleUrl: './player-controls-series-episode.scss'
})
export class PlayerControlsSeriesEpisodeComponent {
  private playerService = inject(PlayerService);
  public skinning = inject(SkinningService);
  public converter = inject(ConverterHelper);
  public settings = inject(SettingsStore);

  public episodeItem = input<IEpisodeDetailsDto>();
  public bookmarkPercentage = input<number>(0);
  public isPlaying = input<boolean>(false);
  public hasOutgoingEnabled = input<boolean>(false);
  public hasUpcomingEnabled = input<boolean>(false);
  public showAutomaticBadge = input<boolean>(true);
  // Emitted only when availability actually flips, so the parent can re-pick the auto-badge episode.
  readonly availabilityStartPassed = output<boolean>();

  public item = this.playerService.getItem();
  public opened = signal(false);
  public BadgeVariant = BadgeVariant;
  private playerItem = computed(() => {
    return this.episodeItem() as any as PlayerItem;
  });

  private isAvailabilityStartPassed = signal<boolean | null>(null);

  play() {
    if(this.showPlayButton()){
      this.playerService.playItem(this.playerItem(), PlayerTypes.Normal);
    }
  }

  showBadge = computed(() => {
    const item = this.episodeItem();
    if (!item) return false;

    const hasRegularBadge = item.badge !== null && item.badge !== undefined;
    const hasOutgoing = this.hasOutgoingEnabled() && !!item.availabilityEndTime;

    // Automatic badge (Nova Epizoda): startTime passed but within automaticBadgeDurationInHours
    const now = Date.now();
    const startMs = item.availabilityStartTime ? new Date(item.availabilityStartTime).getTime() : null;
    const autoWindowMs = (this.settings.automaticBadgeDurationInHours() || 48) * 3600 * 1000;
    const hasAutomatic = this.showAutomaticBadge() && !!startMs && startMs <= now && (startMs + autoWindowMs) > now;

    // Upcoming countdown: startTime is in the future (only when the operator/series enabled it)
    const hasUpcoming = this.hasUpcomingEnabled() && !!startMs && startMs > now;

    return hasRegularBadge || hasOutgoing || hasAutomatic || hasUpcoming;
  });


  showPlayButton = computed(() => {
        const item = this.episodeItem();
        if (!item) return false;

        if (!item.availabilityStartTime) {
            return true;
        }

        const passedSignal = this.isAvailabilityStartPassed();
        if (passedSignal !== null) {
            return passedSignal;
        }

        // Fallback for initial load before badge emits:
        const startTime = new Date(item.availabilityStartTime).getTime();
        const now = new Date().getTime();

        return startTime <= now;
    });

    onAvailabilityStartPassed(passed: boolean) {
        const previous = this.isAvailabilityStartPassed();
        this.isAvailabilityStartPassed.set(passed);
        if (previous !== passed) {
            this.availabilityStartPassed.emit(passed);
        }
    }
}
